import type { SelectHTMLAttributes, ReactNode } from "react";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: ReactNode;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  /** Tighter padding + smaller text for inline/table pickers. Default: false. */
  compact?: boolean;
  /** Class names applied to the outer wrapper div (e.g. `flex-1`). */
  wrapperClassName?: string;
}

export function Select({
  label,
  error,
  options,
  placeholder,
  compact = false,
  className = "",
  wrapperClassName = "",
  id,
  ...props
}: SelectProps) {
  const selectId = id ?? (typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : undefined);

  const selectSize = compact
    ? "px-2 py-1 pr-7 text-xs"
    : "px-3 py-2 pr-9 text-sm";
  const labelSize = compact
    ? "text-xs font-medium text-gray-600 mb-0.5"
    : "text-sm font-medium text-gray-700 mb-1";
  const chevronSize = compact
    ? "right-1.5 h-3.5 w-3.5"
    : "right-2.5 h-4 w-4";

  return (
    <div className={wrapperClassName}>
      {label && (
        <label htmlFor={selectId} className={`block ${labelSize}`}>
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          className={`block w-full appearance-none rounded-lg border bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${selectSize} ${
            error ? "border-red-300" : "border-gray-300"
          } ${className}`}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400 ${chevronSize}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 8l4 4 4-4" />
        </svg>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
